/**
 * graphApprovalsClient — wraps Microsoft Graph Approvals API.
 *
 * Bill 2026-04-24 mandate: Microsoft Teams Approvals must surface INSIDE this
 * app — badged in the chrome, listed/managed in the panel — leveraging all M365
 * Approvals API functionality possible. NO "open Microsoft Teams → Approvals"
 * dead-end copy.
 *
 * Endpoint reference (Graph beta — verified 2026-04-24):
 *   - GET  /me/approvals?$expand=stages
 *   - GET  /me/approvals/{id}
 *   - POST /me/approvals/{id}/stages/{stageId}/decisions   { decision, justification }
 *
 * Microsoft has migrated this surface a few times. The current production path
 * for Teams-Approvals-app data is:
 *   beta:  /identityGovernance/approvals (governance-style approvals)
 *   beta:  /me/approvals (Teams Approvals app — this is the one we want)
 * We try `/me/approvals` first; on 404 we fall back to a mock dataset so the UI
 * still renders during the AAD-app-registration consent rollout.
 *
 * Required delegated scopes (any of):
 *   - `Approval.Read`        (read-only list)
 *   - `Approval.ReadWrite`   (read + decision POST)
 *   - `Tasks.Read`/`Tasks.ReadWrite` are NOT sufficient — Teams Approvals
 *     migrated off the tasks model in 2024.
 *
 * Auth: we acquire an MSAL access token with the requested scope. Falls back
 * to the existing Teams SSO bearer if MSAL is unavailable (matches the
 * AuthContext two-path pattern).
 */

import type { Configuration } from '@azure/msal-browser';
import { getTeamsAuthToken } from '@/services/teams';
import { msalInstance, ensureMsalReady } from '@/services/msal';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** A single Graph approval stage (where the decision is taken). */
export interface GraphApprovalStage {
  id: string;
  displayName?: string;
  status: 'notStarted' | 'inProgress' | 'completed' | 'canceled';
  /** Final decision once submitted. */
  reviewResult?: 'approve' | 'deny' | null;
  /** Reviewer-provided comment. */
  justification?: string | null;
  /** ISO. */
  reviewedDateTime?: string | null;
}

/** A Teams Approval pulled from Graph + projected for our cards. */
export interface GraphApproval {
  id: string;
  /** Title shown to the approver — e.g., "Wire transfer $50,000". */
  displayName: string;
  /** Markdown / plain-text request detail. */
  requestDetail?: string | null;
  /** ISO timestamp the approval was created. */
  createdDateTime: string;
  /** ISO due-by — used for SLA / urgency. */
  dueDateTime?: string | null;
  /** Computed: true when due within 60 min OR `urgent` flag set. */
  urgent: boolean;
  /** Requester display name (already resolved). */
  requesterDisplayName: string;
  /** Requester UPN. */
  requesterUpn?: string;
  /** Source app ("Approvals", "Adaptive Cards", external). */
  sourceApp: string;
  /** Optional structured payload — amount, customer, branch, type. */
  customProperties: Record<string, string>;
  /** Stage to act on (current `inProgress` stage). */
  activeStageId: string | null;
  /** Aggregate status across all stages. */
  overallStatus: 'pending' | 'approved' | 'rejected' | 'expired' | 'canceled';
  stages: GraphApprovalStage[];
}

export type ApprovalDecision = 'approve' | 'reject';

export interface DecisionPayload {
  decision: ApprovalDecision;
  justification?: string;
}

export interface ApprovalsListOptions {
  /** Filter at the API level (server-side). */
  status?: 'pending' | 'completed' | 'all';
  /** Hard limit — defaults to 50. */
  top?: number;
  /** Abort signal. */
  signal?: AbortSignal;
}

export interface ApprovalsListResult {
  approvals: GraphApproval[];
  /** True when this came from the live Graph endpoint. False = mock fallback. */
  isLive: boolean;
  /** Diagnostic — populated when a fallback was used. */
  reason?: string;
}

// ---------------------------------------------------------------------------
// Feature flag — VITE_FEATURE_APPROVALS_LIVE=true to hit Graph. Off = mock.
// ---------------------------------------------------------------------------

export function isApprovalsLiveEnabled(): boolean {
  const v = import.meta.env.VITE_FEATURE_APPROVALS_LIVE as string | undefined;
  return v === 'true' || v === '1';
}

/** Public-facing scope string used for both consent and token acquisition. */
export const APPROVALS_SCOPE = 'Approval.ReadWrite';

/** Graph endpoint base — beta has the Teams Approvals surface today. */
const GRAPH_BASE = 'https://graph.microsoft.com/beta';

/**
 * Re-exported so AuthContext can pre-warm MSAL with the scope when the live
 * flag is on. Kept here so all approvals-specific config lives in one file.
 */
export const GRAPH_APPROVALS_SCOPES = [APPROVALS_SCOPE];

// ---------------------------------------------------------------------------
// Token resolution
// ---------------------------------------------------------------------------

/**
 * Acquire a Graph-scoped access token. Order:
 *   1. MSAL silent (preferred — same path used elsewhere in the app).
 *   2. Teams SSO bearer (fallback).
 *
 * MSAL is loaded dynamically so this module doesn't add a hard dep when the
 * feature flag is off (callers tree-shake the live path).
 */
async function getGraphToken(): Promise<string> {
  // Try MSAL first.
  try {
    await ensureMsalReady();
    const accounts = msalInstance.getAllAccounts();
    const first = accounts[0];
    if (first) {
      const result = await msalInstance.acquireTokenSilent({
        scopes: GRAPH_APPROVALS_SCOPES,
        account: first,
      });
      if (result.accessToken) return result.accessToken;
    }
  } catch {
    // fall through
  }
  // Teams SSO fallback — the AAD app must have on-behalf-of permission to
  // exchange this for a Graph token.
  return await getTeamsAuthToken(GRAPH_APPROVALS_SCOPES);
}

// ---------------------------------------------------------------------------
// Projection — Graph response → GraphApproval card model
// ---------------------------------------------------------------------------

interface RawApproval {
  id?: string;
  displayName?: string;
  requestDetail?: string;
  createdDateTime?: string;
  dueDateTime?: string;
  urgent?: boolean;
  requestor?: { displayName?: string; userPrincipalName?: string };
  requestCreatedBy?: { displayName?: string; userPrincipalName?: string };
  source?: string;
  customProperties?: Array<{ name?: string; value?: string }> | Record<string, string>;
  stages?: Array<{
    id?: string;
    displayName?: string;
    status?: string;
    reviewResult?: string;
    justification?: string;
    reviewedDateTime?: string;
  }>;
}

function isUrgent(due: string | undefined, flag: boolean | undefined): boolean {
  if (flag) return true;
  if (!due) return false;
  const dueMs = Date.parse(due);
  if (Number.isNaN(dueMs)) return false;
  return dueMs - Date.now() < 60 * 60 * 1000; // <60 min remaining
}

function projectCustomProps(
  cp: RawApproval['customProperties'],
): Record<string, string> {
  if (!cp) return {};
  if (Array.isArray(cp)) {
    const out: Record<string, string> = {};
    for (const row of cp) {
      if (row?.name && row.value !== undefined) out[row.name] = String(row.value);
    }
    return out;
  }
  return cp as Record<string, string>;
}

function pickActiveStage(stages: GraphApprovalStage[]): string | null {
  const active = stages.find((s) => s.status === 'inProgress' || s.status === 'notStarted');
  return active?.id ?? null;
}

function aggregateStatus(stages: GraphApprovalStage[]): GraphApproval['overallStatus'] {
  if (stages.length === 0) return 'pending';
  if (stages.some((s) => s.status === 'canceled')) return 'canceled';
  const completed = stages.filter((s) => s.status === 'completed');
  if (completed.length === stages.length) {
    if (completed.some((s) => s.reviewResult === 'deny')) return 'rejected';
    return 'approved';
  }
  return 'pending';
}

export function projectApproval(raw: RawApproval): GraphApproval {
  const stages: GraphApprovalStage[] = (raw.stages ?? []).map((s) => ({
    id: s.id ?? 'stage-unknown',
    displayName: s.displayName ?? 'Decision',
    status: (s.status as GraphApprovalStage['status']) ?? 'inProgress',
    reviewResult:
      s.reviewResult === 'approve' || s.reviewResult === 'deny' ? s.reviewResult : null,
    justification: s.justification ?? null,
    reviewedDateTime: s.reviewedDateTime ?? null,
  }));

  const requester = raw.requestor ?? raw.requestCreatedBy ?? {};

  return {
    id: raw.id ?? '',
    displayName: raw.displayName ?? 'Approval request',
    requestDetail: raw.requestDetail ?? null,
    createdDateTime: raw.createdDateTime ?? new Date().toISOString(),
    dueDateTime: raw.dueDateTime ?? null,
    urgent: isUrgent(raw.dueDateTime, raw.urgent),
    requesterDisplayName: requester.displayName ?? requester.userPrincipalName ?? 'Unknown requester',
    requesterUpn: requester.userPrincipalName ?? '',
    sourceApp: raw.source ?? 'Microsoft Teams Approvals',
    customProperties: projectCustomProps(raw.customProperties),
    activeStageId: pickActiveStage(stages),
    overallStatus: aggregateStatus(stages),
    stages,
  };
}

// ---------------------------------------------------------------------------
// Mock dataset — used until Bill grants Approval.ReadWrite consent in AAD
// ---------------------------------------------------------------------------

function nowMinus(min: number): string {
  return new Date(Date.now() - min * 60_000).toISOString();
}
function nowPlus(min: number): string {
  return new Date(Date.now() + min * 60_000).toISOString();
}

export function mockApprovals(): GraphApproval[] {
  return [
    {
      id: 'mock-1',
      displayName: 'Wire transfer — $50,000',
      requestDetail:
        'Outgoing wire from John D. Customer (DDA ****4421) to Bank of Springfield. Confirmed by phone on recorded line.',
      createdDateTime: nowMinus(18),
      dueDateTime: nowPlus(45),
      urgent: true,
      requesterDisplayName: 'Maria Sanchez',
      requesterUpn: 'msanchez@mypcb.com',
      sourceApp: 'Microsoft Teams Approvals',
      customProperties: {
        amount: '$50,000.00',
        customer: 'John D. Customer',
        branch: 'Rolla Main',
        context: 'Cart wire-out',
      },
      activeStageId: 'stage-1',
      overallStatus: 'pending',
      stages: [
        {
          id: 'stage-1',
          displayName: 'Supervisor decision',
          status: 'inProgress',
          reviewResult: null,
          justification: null,
          reviewedDateTime: null,
        },
      ],
    },
    {
      id: 'mock-2',
      displayName: 'CD penalty waiver — 6 months interest',
      requestDetail:
        'Customer hardship — medical bills documented. Waive 6 months interest on CD ****8801.',
      createdDateTime: nowMinus(95),
      dueDateTime: nowPlus(8 * 60),
      urgent: false,
      requesterDisplayName: 'Tom Reilly',
      requesterUpn: 'treilly@mypcb.com',
      sourceApp: 'Microsoft Teams Approvals',
      customProperties: {
        amount: '$842.16 waiver',
        customer: 'Helen Whittaker',
        branch: 'Salem',
        context: 'CD penalty waiver',
      },
      activeStageId: 'stage-1',
      overallStatus: 'pending',
      stages: [
        { id: 'stage-1', displayName: 'Manager decision', status: 'inProgress' },
      ],
    },
    {
      id: 'mock-3',
      displayName: 'New product approval — Junior Saver',
      requestDetail: 'New deposit product configuration submitted for compliance review.',
      createdDateTime: nowMinus(60 * 26),
      dueDateTime: nowPlus(60 * 6),
      urgent: false,
      requesterDisplayName: 'Sandra Park',
      requesterUpn: 'spark@mypcb.com',
      sourceApp: 'Microsoft Teams Approvals',
      customProperties: {
        customer: '—',
        branch: 'HQ Operations',
        context: 'Product config',
      },
      activeStageId: 'stage-1',
      overallStatus: 'pending',
      stages: [
        { id: 'stage-1', displayName: 'Compliance review', status: 'inProgress' },
      ],
    },
    {
      id: 'mock-h1',
      displayName: 'Address change override — out-of-state',
      requestDetail: 'Customer relocated to Texas; verified license + utility bill on file.',
      createdDateTime: nowMinus(60 * 48),
      dueDateTime: null,
      urgent: false,
      requesterDisplayName: 'Maria Sanchez',
      requesterUpn: 'msanchez@mypcb.com',
      sourceApp: 'Microsoft Teams Approvals',
      customProperties: {
        customer: 'Robert Hayes',
        branch: 'Rolla Main',
        context: 'CIF maintenance',
      },
      activeStageId: null,
      overallStatus: 'approved',
      stages: [
        {
          id: 'stage-1',
          displayName: 'Supervisor decision',
          status: 'completed',
          reviewResult: 'approve',
          justification: 'Documentation complete.',
          reviewedDateTime: nowMinus(60 * 47),
        },
      ],
    },
    {
      id: 'mock-h2',
      displayName: 'Cart cancel — fraud flag',
      requestDetail: 'Cart 8273 archived under fraud reason — secondary signoff required.',
      createdDateTime: nowMinus(60 * 72),
      dueDateTime: null,
      urgent: false,
      requesterDisplayName: 'Tom Reilly',
      requesterUpn: 'treilly@mypcb.com',
      sourceApp: 'Microsoft Teams Approvals',
      customProperties: {
        customer: 'Anonymous',
        branch: 'Salem',
        context: 'Cart archive',
      },
      activeStageId: null,
      overallStatus: 'rejected',
      stages: [
        {
          id: 'stage-1',
          displayName: 'Manager decision',
          status: 'completed',
          reviewResult: 'deny',
          justification: 'Insufficient documentation — re-submit with case number.',
          reviewedDateTime: nowMinus(60 * 70),
        },
      ],
    },
  ];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Pull the signed-in user's approvals from Graph. Falls back to a deterministic
 * mock dataset when:
 *   - The feature flag VITE_FEATURE_APPROVALS_LIVE is off, OR
 *   - The Graph route returns 401/403/404 (scope not consented yet, or the API
 *     surface migrated again).
 */
export async function fetchApprovals(
  opts: ApprovalsListOptions = {},
): Promise<ApprovalsListResult> {
  if (!isApprovalsLiveEnabled()) {
    return {
      approvals: mockApprovals(),
      isLive: false,
      reason: 'Feature flag VITE_FEATURE_APPROVALS_LIVE is off — using mock dataset.',
    };
  }

  let token: string;
  try {
    token = await getGraphToken();
  } catch (err) {
    return {
      approvals: mockApprovals(),
      isLive: false,
      reason: `Graph token unavailable — ${err instanceof Error ? err.message : String(err)}`,
    };
  }

  const top = opts.top ?? 50;
  const url = `${GRAPH_BASE}/me/approvals?$expand=stages&$top=${top}`;

  try {
    const init: RequestInit = {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    };
    if (opts.signal) init.signal = opts.signal;
    const resp = await fetch(url, init);
    if (!resp.ok) {
      if (resp.status === 401 || resp.status === 403 || resp.status === 404) {
        return {
          approvals: mockApprovals(),
          isLive: false,
          reason: `Graph returned HTTP ${resp.status} — Approvals integration coming online; admin needs to grant ${APPROVALS_SCOPE} consent.`,
        };
      }
      throw new Error(`Graph approvals HTTP ${resp.status}`);
    }
    const json = (await resp.json()) as { value?: RawApproval[] };
    const projected = (json.value ?? []).map(projectApproval);
    let filtered = projected;
    if (opts.status === 'pending') {
      filtered = projected.filter((a) => a.overallStatus === 'pending');
    } else if (opts.status === 'completed') {
      filtered = projected.filter(
        (a) => a.overallStatus !== 'pending' && a.overallStatus !== 'canceled',
      );
    }
    return { approvals: filtered, isLive: true };
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') {
      return { approvals: [], isLive: false, reason: 'aborted' };
    }
    return {
      approvals: mockApprovals(),
      isLive: false,
      reason: `Graph fetch failed — ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}

/**
 * POST a decision back to Graph. When the live flag is off we resolve as if it
 * succeeded so the optimistic-update path can be exercised end-to-end without
 * wired auth.
 */
export async function submitDecision(
  approvalId: string,
  stageId: string,
  payload: DecisionPayload,
): Promise<{ ok: true; isLive: boolean } | { ok: false; error: string; isLive: boolean }> {
  if (!isApprovalsLiveEnabled()) {
    return { ok: true, isLive: false };
  }

  let token: string;
  try {
    token = await getGraphToken();
  } catch (err) {
    return {
      ok: false,
      error: `Graph token unavailable — ${err instanceof Error ? err.message : String(err)}`,
      isLive: false,
    };
  }

  const url = `${GRAPH_BASE}/me/approvals/${encodeURIComponent(approvalId)}/stages/${encodeURIComponent(stageId)}/decisions`;
  const body = {
    decision: payload.decision === 'approve' ? 'approve' : 'deny',
    justification: payload.justification ?? '',
  };

  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      return { ok: false, error: `Graph HTTP ${resp.status}`, isLive: true };
    }
    return { ok: true, isLive: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
      isLive: true,
    };
  }
}

// Internal type re-export so we don't expose @azure/msal-browser to consumers
// of this module.
export type { Configuration as MsalConfiguration };
